import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { Mail, Phone, Facebook, Twitter, Linkedin, Instagram, Smile, Star, FileCheck } from 'lucide-react';

const FALLBACK_LOGO = '/logo.png';

const Footer = () => {
  const { settings } = useSettings();
  
  // Use logo URL directly (external URL) with fallback to local logo
  const logoUrl = settings.logo_url || FALLBACK_LOGO;

  const getSocialIcon = (platform) => {
    const icons = {
      'Facebook': Facebook,
      'Twitter': Twitter,
      'LinkedIn': Linkedin,
      'Instagram': Instagram
    };
    return icons[platform] || Facebook;
  };

  return (
    <footer className="bg-white border-t border-secondary-200" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand, Social & Contact */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-6">
              <img 
                src={logoUrl} 
                alt={`${settings.company_name} Logo`} 
                className="h-24 w-auto object-contain"
                onError={(e) => { e.target.src = FALLBACK_LOGO; }}
              />
            </div>

            {/* Social Media */}
            {settings.social_media && settings.social_media.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  {settings.social_media.map((social, index) => {
                    const Icon = getSocialIcon(social.platform);
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 bg-secondary-200 hover:bg-primary-600 hover:text-white text-secondary-700 rounded-md flex items-center justify-center transition-all duration-200"
                        aria-label={social.platform}
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="space-y-3">
              {settings.footer_email && (
                <div className="flex items-start gap-2 text-sm text-secondary-700">
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-xs text-secondary-500">Email</p>
                    <p className="text-secondary-900">{settings.footer_email}</p>
                  </div>
                </div>
              )}
              {settings.footer_phone && (
                <div className="flex items-start gap-2 text-sm text-secondary-700">
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-xs text-secondary-500">Call</p>
                    <p className="text-secondary-900">{settings.footer_phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Footer Columns */}
          {settings.footer_columns && settings.footer_columns.slice(0, 3).map((column, index) => (
            <div key={index}>
              <h3 className="text-sm font-bold text-secondary-900 mb-4 uppercase tracking-wide">{column.title}</h3>
              <ul className="space-y-2.5">
                {column.links && column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link 
                      to={link.url} 
                      className="text-secondary-600 hover:text-primary-600 transition-colors text-sm"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Social Proof Stats - Right Side */}
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Smile className="h-5 w-5 text-secondary-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900 font-mono">{settings.stats?.[0]?.value || '10,000+'}</p>
                <p className="text-xs text-secondary-600">{settings.stats?.[0]?.label || 'Happy Customers'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900 font-mono">{settings.stats?.[1]?.value || '4.8'}</p>
                <p className="text-xs text-secondary-600">{settings.stats?.[1]?.label || 'Rated on Google'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FileCheck className="h-5 w-5 text-secondary-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900 font-mono">{settings.stats?.[2]?.value || '500+'}</p>
                <p className="text-xs text-secondary-600">{settings.stats?.[2]?.label || 'Claims Served'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Comprehensive Disclaimer & Regulatory Section - Side by Side */}
        {(settings.footer_disclaimer_full || settings.company_full_name) && (
          <div className="border-t border-secondary-200 pt-6 pb-6 bg-secondary-50">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Full Disclaimer (2/3 width) */}
              <div className="lg:col-span-2 space-y-4">
                {settings.footer_disclaimer_full && (
                  <p className="text-xs text-secondary-600 leading-relaxed">
                    {settings.footer_disclaimer_full}
                  </p>
                )}
                {settings.ndnc_authorization && (
                  <p className="text-xs text-secondary-700 font-medium">
                    {settings.ndnc_authorization}
                  </p>
                )}
              </div>

              {/* Right: Company & Regulatory Details (1/3 width) */}
              <div className="space-y-2">
                {settings.company_full_name && (
                  <p className="text-sm font-bold text-secondary-900">
                    {settings.company_full_name}
                  </p>
                )}
                <div className="text-xs text-secondary-600 space-y-1">
                  {settings.irdai_license && (
                    <p>
                      <span className="font-semibold text-secondary-900">IRDAI License Number: </span>
                      {settings.irdai_license}
                    </p>
                  )}
                  {settings.cin && (
                    <p>
                      <span className="font-semibold text-secondary-900">CIN: </span>
                      {settings.cin}
                      {settings.broker_type && <span> | {settings.broker_type}</span>}
                    </p>
                  )}
                  {settings.license_valid_till && (
                    <p>
                      <span className="font-semibold text-secondary-900">Valid Till: </span>
                      {settings.license_valid_till}
                    </p>
                  )}
                  {settings.principal_officer && (
                    <p>
                      <span className="font-semibold text-secondary-900">Principal Officer: </span>
                      {settings.principal_officer}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copyright */}
        <div className="border-t border-secondary-200 pt-6 text-center">
          <p className="text-secondary-600 text-xs">
            © {new Date().getFullYear()} {settings.company_name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
